interface Attributes {
  [key: string]: string | number | boolean | Date;
}

interface Converter {
  read: (value: string) => string;
  write: (value: string) => string;
}

export const defaultConverter: Converter = {
  read(value: string): string {
    if (value[0] === '"') {
      value = value.slice(1, -1);
    }
    return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);
  },
  write(value: string): string {
    return encodeURIComponent(value).replace(
      /%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
      decodeURIComponent,
    );
  },
};

export class CookieManager {
  private readonly converter: Converter;
  private readonly defaultAttributes: Attributes;
  constructor(converter: Converter, defaultAttributes: Attributes) {
    this.converter = converter;
    this.defaultAttributes = defaultAttributes;
  }

  set<T>(name: string, value: string | T, attributes: Attributes): string {
    if (typeof document === "undefined") {
      return "";
    }

    if (typeof value === "object") {
      try {
        value = JSON.stringify(value);
      } catch {
        value = (value?.toString() ?? "") || "";
      }
    }
    attributes = Object.assign({}, this.defaultAttributes, attributes);

    if (typeof attributes.expires === "number") {
      attributes.expires = new Date(Date.now() + attributes.expires * 864e5);
    }
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (attributes.expires) {
      attributes.expires = (attributes.expires as Date).toUTCString();
    }

    name = encodeURIComponent(name)
      .replace(/%(2[346B]|5E|60|7C)/g, decodeURIComponent)
      .replace(/[()]/g, decodeURIComponent);

    let stringifiedAttributes = "";
    for (const attributeName in attributes) {
      if (
        !Object.prototype.hasOwnProperty.call(attributes, attributeName) ||
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        !attributes[attributeName]
      ) {
        continue;
      }
      stringifiedAttributes += "; " + attributeName;
      if (attributes[attributeName] === true) {
        continue;
      }
      stringifiedAttributes +=
        "=" + (attributes[attributeName] as string).split(";")[0];
    }
    return (document.cookie =
      name +
      "=" +
      this.converter.write(value as string) +
      stringifiedAttributes);
  }

  get<T>(name?: string): T {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (typeof document === "undefined" || (arguments.length && !name)) {
      return "" as T;
    }

    const cookies = document.cookie ? document.cookie.split("; ") : [];
    const jar: { [key: string]: string } = {};
    for (const cookie of cookies) {
      const parts = cookie.split("=");
      const value = parts.slice(1).join("=");

      try {
        const found = decodeURIComponent(parts[0]);
        if (!(found in jar)) {
          jar[found] = this.converter.read(value);
        }
        if (name === found) {
          break;
        }
      } catch {
        // Do nothing...
      }
    }

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    let result = name ? jar[name] : jar;
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      result = JSON.parse(result as string);
    } catch {
      // Do nothing...
    }
    return result as T;
  }

  remove(name: string, attributes: Attributes): void {
    this.set(
      name,
      "",
      Object.assign({}, attributes, {
        expires: -1,
      }),
    );
  }

  all(): { [key: string]: string | object } {
    if (document.cookie === "") {
      return {};
    }
    const cookies = document.cookie.split("; ");
    const result: { [key: string]: string | object } = {};
    let i = 0;
    const l = cookies.length;
    for (; i < l; i++) {
      const item: string[] = cookies[i].split("=");
      const key = decodeURIComponent(item.shift() as string);
      const value = decodeURIComponent(item.join("="));
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        result[key] = JSON.parse(value);
      } catch {
        result[key] = value;
      }
    }
    return result;
  }

  withAttributes(attributes: Attributes): CookieManager {
    return new CookieManager(
      this.converter,
      Object.assign({}, this.defaultAttributes, attributes),
    );
  }

  withConverter(newConverter: Converter): CookieManager {
    return new CookieManager(newConverter, this.defaultAttributes);
  }
}

const $cookie = new CookieManager(defaultConverter, { path: "/" });
export { $cookie };
